import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { notifyError, notifySuccess } from '../lib/toast';
import { resetPassword } from '../services/auth.service';
import { Logo } from '../components/shared/Logo';
import { Icon } from '../components/shared/Icons';
import { Input } from '../components/shared/Input';
import { Field } from '../components/shared/Field';
import { Button } from '../components/shared/Button';
import { Spinner } from '../components/shared/Spinner';

// Form-only schema: mirrors resetPasswordSchema's password rule + a confirm field. The token
// comes from the URL, not the form.
const resetFormSchema = z
  .object({
    password: z.string().min(8, 'At least 8 characters').max(72),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  });

type ResetFormValues = z.infer<typeof resetFormSchema>;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetFormValues>({
    resolver: zodResolver(resetFormSchema),
    defaultValues: { password: '', confirm: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await resetPassword({ token, password: values.password });
      notifySuccess('Password updated — sign in with your new password');
      navigate('/login', { replace: true });
    } catch (err) {
      // Invalid / expired / used token surfaces here as the action's outcome.
      notifyError(err, 'Could not reset your password');
    }
  });

  return (
    <div className="grid min-h-screen place-items-center bg-bg p-6">
      <div className="w-full max-w-[400px]">
        <div className="mb-7 flex justify-center">
          <Logo size={22} />
        </div>

        {!token ? (
          // No token → the link is malformed; send them back to request a fresh one.
          <div className="rounded border border-line bg-surface p-6 text-center shadow-1">
            <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-sm bg-accent-tint text-accent">
              <Icon.lock className="w-[18px] h-[18px]" />
            </div>
            <h2 className="text-lg text-ink">This reset link is incomplete</h2>
            <p className="mt-1.5 text-[13px] text-muted">
              The link is missing its token. Request a new password-reset email and try again.
            </p>
            <Link to="/forgot-password" className="mt-4 inline-block text-[13px] font-medium text-accent">
              Request a new link
            </Link>
          </div>
        ) : (
          <div className="rounded border border-line bg-surface p-6 shadow-1">
            <h2 className="text-xl text-ink">Choose a new password</h2>
            <p className="mb-5 mt-1 text-[13px] text-muted">Set a new password for your ProRef account.</p>

            <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
              <Field label="New password" htmlFor="password" error={errors.password?.message}>
                <Input id="password" type="password" autoComplete="new-password" placeholder="At least 8 characters" {...register('password')} />
              </Field>
              <Field label="Confirm password" htmlFor="confirm" error={errors.confirm?.message}>
                <Input id="confirm" type="password" autoComplete="new-password" placeholder="••••••••" {...register('confirm')} />
              </Field>

              <Button type="submit" variant="accent" size="lg" block disabled={isSubmitting} className="mt-1">
                {isSubmitting ? <Spinner className="w-4 h-4" /> : <>Update password <Icon.arrow className="w-3.5 h-3.5" /></>}
              </Button>
            </form>

            <p className="mt-4 text-center text-[12px] text-muted">
              <Link to="/login" className="font-medium text-accent">
                Back to sign in
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
